/* eslint-disable max-lines */
import { DeviceStatusPopover } from '@/app-shell/chronicle/components/device-status-popover';
import { RemoteDeviceSvg } from '@/assets/icons/remote-device-svg';
import {
  chronicleConnectionContextAtom,
  isChronicleConnectedAtom,
  usbConnectedAtom,
} from '@/atoms/chronicle';
import {
  scribeLayoutTourEligibleAtom,
  scribeLayoutTourRelaunchRequestedAtom,
  useQueryRecentSessionEngagement,
} from '@heidi/session/data';

import {
  allPrimaryItemsCollapsedAtom,
  dictateCollapsedIntoPrimaryAtom,
  discoveryCallCollapsedIntoPrimaryAtom,
  helpCollapsedIntoPrimaryAtom,
  helpDropdownOpenAtom,
  notificationsCollapsedIntoPrimaryAtom,
  pendingNavHrefAtom,
} from '@/atoms/sideNavAtoms';
import { useOfflineGatedTooltip } from '@/components/offline-recording/use-offline-gated-tooltip';
import { dictationLogsSpotlightPorts } from '@/composition/hosts/dictation/spotlights/dictation-sidebar-spotlight-ports';
import { useCurrentUserQueryResult } from '@/context/current-user-context';
import {
  IS_DICTATION_LOGS_VISIBLE,
  useIsDesktopChronicleEnabled,
  useIsFlagCohortResolved,
  useIsRibbonSidebarEnabled,
  useIsSessionPageV2Enabled,
  useIsSidebarIAEnabled,
} from '@/features/flags';
import { getHelpCenterHomeUrl } from '@/helpers/getHelpCenterUrl';
import { useQueryNotificationPreferences } from '@/hooks/query/useQueryNotificationPreferences';
import { useQueryNotifications } from '@/hooks/query/useQueryNotifications';
import { useQuerySystemVersion } from '@/hooks/query/useQuerySystemVersion';
import { useCurrentSessionId } from '@/hooks/useCurrentIds';
import { useIntercom } from '@/hooks/useIntercom';
import { useIsEvidenceOnlyUser } from '@/hooks/useIsEvidenceOnlyUser';
import { useIsUKOrEU } from '@/hooks/useIsUKOrEU';
import { navigationHelp, navigationRoadmap } from '@/lib/analytics/scribe/navigation';
import { cn } from '@/lib/cn';
import { isTauri } from '@/tauri/utils';
import { getDesktopVersion } from '@/utils/appVersion';
import { isOnPrem } from '@/utils/buildProfile';
import { isReferralProgramAvailableForLocale } from '@/utils/i18n/referralProgramLocale';
import { mapLocaleAliasToSupportedLocale } from '@/utils/i18n/supportedLocales';
import { isIntercomDisabledForLocale } from '@/utils/intercomLocale';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { Kbd } from '@heidi/design-system/kbd';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@heidi/design-system/page-shell';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@heidi/design-system/popover';
import { TypographyCaption } from '@heidi/design-system/typography';
import { DictationLogsSpotlight } from '@heidi/dictate';
import { isAffiliateLinkModalOpenAtom } from '@heidi/growth';
import { useTranslations } from '@heidi/runtime/i18n';
import { NotificationChannel, NotificationStatus, Region } from '@heidi/schema/ml-scribe';

import { useAtomValue, useSetAtom } from 'jotai';
import {
  BadgeDollarSignIcon,
  BellIcon,
  CommandIcon,
  HelpCircleIcon,
  InfoIcon,
  LibraryBigIcon,
  LightbulbIcon,
  MessageCircleIcon,
  SparklesIcon,
  Speech,
} from 'lucide-react';
import { m, useReducedMotion } from 'motion/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { isShortcutsSheetOpenAtom } from '../ShortcutsHelper/shortcutsAtom';
import { useIsRibbonRail } from './hooks/useIsRibbonRail';
import { useIsRouteActive } from './hooks/useIsRouteActive';
import { NotificationsPanelAction, NotificationsPanelContent } from './mobile/panels';
import { useMobilePanel } from './mobile/use-mobile-panel';
import { NavItem } from './sidebar';
import { CollapsedFooterAnchor } from './sidebar/footer-rail/collapsed-footer-anchor';
import {
  useFooterRailCollapsible,
  useFooterRailRegistry,
  type FooterMoreRowDescriptor,
} from './sidebar/footer-rail/footer-rail-context';
import { FOOTER_COLLAPSE_EXIT_MS } from './sidebar/footer-rail/footer-rail.constants';
import { useCommitAfterCollapseExit } from './sidebar/footer-rail/use-commit-after-collapse-exit';

const SUPPORT_CONTACT_URL_BY_LOCALE: Record<string, string> = {
  'ja-jp': 'https://www.heidihealth.com/ja-jp/contact',
};

const getSupportContactUrlForLocale = (locale?: string | null) => {
  const canonicalLocale = mapLocaleAliasToSupportedLocale(locale);

  return canonicalLocale
    ? SUPPORT_CONTACT_URL_BY_LOCALE[canonicalLocale.toLowerCase()]
    : undefined;
};

/**
 * Wraps the bell in `<PopoverTrigger asChild>` on desktop so the
 * notifications Popover Root hoisted in `SideNavFooter` can anchor to
 * it. On mobile the Popover Root is not mounted (the bell opens a
 * mobile sheet instead), so `<PopoverTrigger>` would throw for lack of
 * an ancestor Popover context — passthrough instead.
 */
const NotificationsBellTrigger = ({
  isMobile,
  children,
}: {
  isMobile: boolean;
  children: React.ReactNode;
}) => (isMobile ? <>{children}</> : <PopoverTrigger asChild>{children}</PopoverTrigger>);

interface FooterIconButtonsProps {
  isMobile: boolean;
  /** Render as vertical nav items (default) or horizontal icon buttons with stacking animation. */
  verticalNavItems?: boolean;
}

/**
 * Footer icon buttons with Help dropdown and Notifications button.
 *
 * Three modes:
 * - Label-icons (auto): When collapsible='label-icons' on desktop, renders compact 36px icon-only buttons with tooltips
 * - verticalNavItems={true} (default): Renders as standard vertical nav items matching rest of sidebar
 * - verticalNavItems={false}: Renders as horizontal icon buttons with stacking animation when collapsed
 *
 * Consolidated items in Help dropdown:
 * - Launch new layout tour — Session Page V2 users on desktop
 * - Get support (Intercom) — cloud only
 * - Help Center
 * - Request a feature (Canny) — cloud only
 * - Shortcuts
 * - Refer and earn — cloud only
 * - About — GB (UK region) only; links to the Regulatory Information page
 *
 * On-prem builds hide the three cloud-only items (their backing services are
 * disabled/stripped on-prem), leaving Help Center and Shortcuts.
 */
export function FooterIconButtons({
  isMobile,
  verticalNavItems = true,
}: FooterIconButtonsProps) {
  const { t } = useTranslations();
  const intl = useIntl();
  // APP-6672: the footer icon buttons (Devices, Dictation logs, Help,
  // Notifications) are all server-backed, so disable them offline with the
  // "Not available offline" tooltip. No-op on web / online / prod.
  const { isOfflineGated, gatedTooltip } = useOfflineGatedTooltip();
  const { state, collapsible } = useSidebar();
  const prefersReducedMotion = useReducedMotion();
  const isRibbonSidebarEnabled = useIsRibbonSidebarEnabled();
  const isSessionPageV2Enabled = useIsSessionPageV2Enabled();
  const isSessionPageV2FlagResolved = useIsFlagCohortResolved();
  const { isSuccess: isCurrentUserResolved } = useCurrentUserQueryResult(() => true);
  const canLaunchScribeLayoutTour =
    isCurrentUserResolved && isSessionPageV2FlagResolved && isSessionPageV2Enabled;
  const dropdownSideOffset = isRibbonSidebarEnabled && !isMobile ? 12 : 4;

  // This footer is shared by web and desktop, but the version each should show
  // differs, so the source is chosen per-platform:
  //   - Web: the system/web release version fetched from system-version.json.
  //   - Desktop: the actual Tauri app version (getVersion()), NOT the web
  //     version — showing the web version on desktop is misleading since it
  //     never matches the installed desktop build.
  // The remote system-version fetch is skipped entirely on desktop so we don't
  // fire a request whose result we'd never display.
  const isDesktop = isTauri();
  const { data: systemVersion } = useQuerySystemVersion({
    enabled: !isDesktop,
  });
  const displayVersion = isDesktop ? getDesktopVersion() : systemVersion;

  // Intercom
  const { isIntercomOpen, showMessageModal, hideMessageModal } = useIntercom();

  // Notifications. The bell drives one of two surfaces depending on
  // viewport:
  //   - Mobile: opens the mobile panel sheet via `useMobilePanel`.
  //   - Desktop: the bell is wrapped in `<PopoverTrigger asChild>` and
  //     the Popover Root is hoisted to `SideNavFooter`; Radix owns
  //     open/close so the bell's onClick is a no-op on desktop.
  const { openPanel, isInMobileProvider } = useMobilePanel();
  const { data: notifications } = useQueryNotifications();
  const { data: notificationPreferencesData } = useQueryNotificationPreferences();
  const isInmailNotificationEnabled =
    notificationPreferencesData?.subscribed_notification_channels?.includes(
      NotificationChannel.INMAIL
    );
  const hasUnreadNotifications =
    isInmailNotificationEnabled &&
    notifications?.some(
      (notification) => notification.status === NotificationStatus.UNREAD
    );

  // User-triggered V2 layout tour. The request stays outside the modal queue
  // until the wide V2 session view is mounted, so route transitions and compact
  // layouts cannot leave an unrenderable tour blocking other queued surfaces.
  const requestScribeLayoutTourRelaunch = useSetAtom(
    scribeLayoutTourRelaunchRequestedAtom
  );
  const isScribeLayoutTourEligible = useAtomValue(scribeLayoutTourEligibleAtom);
  const recentSessionsQuery = useQueryRecentSessionEngagement(false);

  // Shortcuts sheet
  const setIsShortcutsSheetOpen = useSetAtom(isShortcutsSheetOpenAtom);

  // Affiliate modal
  const setIsAffiliateLinkModalOpen = useSetAtom(isAffiliateLinkModalOpenAtom);
  const { isEvidenceOnlyUser } = useIsEvidenceOnlyUser();
  const { isUKOrEU, region } = useIsUKOrEU();
  // GB-only "About" row: links to the UK-targeted Regulatory Information page
  // (medical-device build). Shown only for a confirmed UK Kinde region and never
  // on-prem — the regulatory link stays cloud-only, matching the Settings footer.
  // EU/other regions and the still-resolving state fall through to hidden (region
  // is undefined until the query settles), so there's no flash.
  const showAboutHelpItem = region === Region.UK && !isOnPrem;
  const earnBonusText = isEvidenceOnlyUser
    ? t('sidenav.evidenceOnly.earnBonus')
    : t('sidenav.earnBonus');

  // Chronicle device state
  const router = useRouter();
  const supportContactUrl = getSupportContactUrlForLocale(intl.locale);
  const isIntercomDisabled = isIntercomDisabledForLocale(intl.locale);
  const isChronicleEnabled = useIsDesktopChronicleEnabled();
  const currentSessionId = useCurrentSessionId();
  const isOnV2SessionPage =
    router.pathname.startsWith('/scribe/sessions') && !!currentSessionId;
  const showLayoutTourItem =
    canLaunchScribeLayoutTour &&
    !isMobile &&
    (!isOnV2SessionPage || isScribeLayoutTourEligible);
  const isWeb = !isTauri();
  // Heidi Remote entry: always shown on web (routes to the download guide since
  // Heidi Remote is desktop-only, APP-7445), flag-gated on desktop. No linked
  // device on web, so the status popover never opens and the click falls
  // through to `router.push('/devices')`. Not on on-prem web — the guide links
  // to public heidihealth.com, unreachable on an airgapped network.
  const showDeviceNavEntry = (isWeb && !isOnPrem) || isChronicleEnabled;
  const isChronicleConnected = useAtomValue(isChronicleConnectedAtom);
  const isUsbConnected = useAtomValue(usbConnectedAtom);
  const connectionContext = useAtomValue(chronicleConnectionContextAtom);
  const hasLinkedDevice = connectionContext.linkedDeviceId !== null;
  const [isDevicePopoverOpen, setIsDevicePopoverOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const triggerHelp = useAtomValue(helpDropdownOpenAtom);
  const setHelpDropdownOpen = useSetAtom(helpDropdownOpenAtom);
  useEffect(() => {
    if (triggerHelp) {
      setIsHelpOpen(true);
      setHelpDropdownOpen(false);
    }
  }, [triggerHelp, setHelpDropdownOpen]);

  const showDevicePopover = hasLinkedDevice && state === 'collapsed';
  const isDictationLogsEnabled = IS_DICTATION_LOGS_VISIBLE();
  const isRouteActive = useIsRouteActive();
  const setPendingNavHref = useSetAtom(pendingNavHrefAtom);
  const dictationLogsLabel = intl.formatMessage({
    defaultMessage: 'Dictate',
    id: '0jFAdH4uFc',
    description: 'Sidebar nav label and tooltip for the dictation history page',
  });
  const devicesLabel = intl.formatMessage({
    defaultMessage: 'Remote',
    id: 'JJBzhXu7JU',
  });

  // Remote and Dictate self-hide when they collapse into the footer More.
  const isRibbonRail = useIsRibbonRail();
  const isSidebarIAEnabled = useIsSidebarIAEnabled();
  // Sidebar IA (MUL-3702) moves Remote into Settings on web. Desktop keeps it in
  // the footer (MUL-3755) because the tile is where users see connection status.
  const showRemoteInFooter = showDeviceNavEntry && !(isSidebarIAEnabled && isWeb);

  // Supply the translated "More" label to the footer-rail tile (rendered in
  // SideNavFooter, which can't hold a `t()` site) from this existing translations
  // consumer — reuses the frozen-catalog `common.more` rather than a new string.
  const { setMoreLabel } = useFooterRailRegistry();
  useEffect(() => {
    setMoreLabel(t('common.more'));
  }, [setMoreLabel, t]);

  const isRemoteRouteActive = isRouteActive('/devices');
  const remoteDescriptor = useMemo<FooterMoreRowDescriptor>(
    () => ({
      id: 'remote',
      label: devicesLabel,
      icon: <RemoteDeviceSvg className="h-5 w-4" />,
      testId: 'sidebar-footer-devices',
      href: '/devices',
      isActive: isRemoteRouteActive,
    }),
    [devicesLabel, isRemoteRouteActive]
  );
  // Never collapse Remote while a device is linked: its live-status popover must
  // stay anchored to a real tile, not a closed dropdown row.
  // Sidebar IA (MUL-3702, MUL-3755): web skips registration so Remote never
  // appears in the rail (it lives in Settings). Desktop always pins it, since the
  // IA footer has no More tile for a collapsed Remote to land in.
  const isRemoteCollapsed = useFooterRailCollapsible({
    id: 'remote',
    visible: showRemoteInFooter && isRibbonRail,
    pinned: hasLinkedDevice || isSidebarIAEnabled,
    descriptor: remoteDescriptor,
  });

  const isDictateRouteActive = isRouteActive('/dictation-logs');
  const dictateDescriptor = useMemo<FooterMoreRowDescriptor>(
    () => ({
      id: 'dictate',
      label: dictationLogsLabel,
      icon: <Speech />,
      testId: 'sidebar-footer-dictation-logs',
      href: '/dictation-logs',
      isActive: isDictateRouteActive,
    }),
    [dictationLogsLabel, isDictateRouteActive]
  );
  // The footer rail only starts collapsing after every primary nav fixed item
  // has folded into More (very small sidebar, sidebar IA).
  const allPrimaryCollapsed = useAtomValue(allPrimaryItemsCollapsedAtom);
  // Cascade gate: Dictate is position 2 (after Discovery Call) — it only
  // enters the budget once Discovery Call has committed its collapse.
  const discoveryCallCommitted = useAtomValue(discoveryCallCollapsedIntoPrimaryAtom);
  const isDictateCollapsed = useFooterRailCollapsible({
    id: 'dictate',
    visible:
      isRibbonRail &&
      isDictationLogsEnabled &&
      (!isSidebarIAEnabled || (allPrimaryCollapsed && discoveryCallCommitted)),
    pinned: false,
    descriptor: dictateDescriptor,
  });

  // When IA flag is on and Dictate has been collapsed by the footer rail budget,
  // signal to the primary nav More that it should include Dictate.
  // Commit each collapse only after its exit animation finishes, so the next
  // item's gate (the previous item's committed atom) can't pass mid-flight —
  // that is what sequences the cascade one item at a time.
  const setDictateCollapsedIntoPrimary = useSetAtom(dictateCollapsedIntoPrimaryAtom);
  useCommitAfterCollapseExit(
    isSidebarIAEnabled && isDictateCollapsed,
    setDictateCollapsedIntoPrimary,
    FOOTER_COLLAPSE_EXIT_MS
  );
  // Help — same rail treatment under the sidebar IA flag: the budget collapse
  // relocates the tile into the primary nav More instead of a footer More.
  const helpDescriptor = useMemo<FooterMoreRowDescriptor>(
    () => ({
      id: 'help',
      label: t('shared.Help'),
      icon: <HelpCircleIcon />,
      testId: 'sidebar-footer-help',
      href: getHelpCenterHomeUrl(intl.locale),
      external: true,
    }),
    [t, intl.locale]
  );
  const isHelpCollapsed = useFooterRailCollapsible({
    id: 'help',
    visible: isSidebarIAEnabled && allPrimaryCollapsed && isRibbonRail,
    pinned: false,
    descriptor: helpDescriptor,
  });
  const setHelpCollapsedIntoPrimary = useSetAtom(helpCollapsedIntoPrimaryAtom);
  useCommitAfterCollapseExit(
    isSidebarIAEnabled && isHelpCollapsed,
    setHelpCollapsedIntoPrimary,
    FOOTER_COLLAPSE_EXIT_MS
  );

  const notificationsDescriptor = useMemo<FooterMoreRowDescriptor>(
    () => ({
      id: 'notifications',
      label: t('sidenav.notifications'),
      icon: <BellIcon />,
      testId: 'sidebar-footer-notifications',
    }),
    [t]
  );
  const isNotificationsCollapsed = useFooterRailCollapsible({
    id: 'notifications',
    visible: isSidebarIAEnabled && allPrimaryCollapsed && isRibbonRail,
    pinned: false,
    descriptor: notificationsDescriptor,
  });
  const setNotificationsCollapsedIntoPrimary = useSetAtom(
    notificationsCollapsedIntoPrimaryAtom
  );
  useCommitAfterCollapseExit(
    isSidebarIAEnabled && isNotificationsCollapsed,
    setNotificationsCollapsedIntoPrimary,
    FOOTER_COLLAPSE_EXIT_MS
  );
  // A tile folds only after the PREVIOUS item has committed its collapse (its
  // 180ms exit finished) — the budget would otherwise hide the whole tail at
  // once. The first item (Dictate) gates on the budget alone.
  const dictateCommitted = useAtomValue(dictateCollapsedIntoPrimaryAtom);
  const helpCommitted = useAtomValue(helpCollapsedIntoPrimaryAtom);
  const isHelpHidden = isHelpCollapsed && dictateCommitted;
  const isNotificationsHidden = isNotificationsCollapsed && helpCommitted;
  // The primary nav More takes the Dictate spotlight on this same commit.
  const hostsDictateSpotlight = !(isSidebarIAEnabled && dictateCommitted);

  // Reset popover when conditions change (e.g. sidebar expands or device unlinked)
  useEffect(() => {
    if (!showDevicePopover) setIsDevicePopoverOpen(false);
  }, [showDevicePopover]);

  const handleDevicePopoverOpenChange = (open: boolean) => {
    if (showDevicePopover) {
      setIsDevicePopoverOpen(open);
    } else if (open) {
      // No linked device or expanded sidebar — navigate directly.
      // Prime the optimistic active state so the button highlights during
      // the route transition, matching the Dictate entry's Link onClick.
      setPendingNavHref('/devices');
      void router.push('/devices');
    }
  };

  const handleNavigateToDevices = () => {
    setIsDevicePopoverOpen(false);
    setPendingNavHref('/devices');
    void router.push('/devices');
  };

  const handleHelpClick = () => {
    if (isIntercomDisabled) return;

    if (isIntercomOpen) {
      hideMessageModal();
    } else {
      navigationHelp();
      showMessageModal();
    }
  };

  const handleNotificationsClick = () => {
    // Mobile: open the mobile panel sheet. Desktop is handled by the
    // enclosing `<Popover>` in `SideNavFooter` via `<PopoverTrigger>`,
    // so there's nothing to do here.
    if (isMobile && isInMobileProvider) {
      openPanel(<NotificationsPanelContent />, <NotificationsPanelAction />);
    }
  };

  const handleLaunchScribeLayoutTour = async () => {
    if (isOnV2SessionPage) {
      requestScribeLayoutTourRelaunch(true);
      return;
    }

    let lastSessionId = recentSessionsQuery.data?.pages.at(0)?.sessions.at(0)?.session_id;
    if (!lastSessionId) {
      const result = await recentSessionsQuery.refetch();
      lastSessionId = result.data?.pages.at(0)?.sessions.at(0)?.session_id;
    }

    // There is no session layout (and therefore no tour anchors) for an empty
    // account. Land on the session list without putting an inert item in the
    // modal queue.
    if (!lastSessionId) {
      await router.push('/scribe/sessions');
      return;
    }

    requestScribeLayoutTourRelaunch(true);
    try {
      const didNavigate = await router.push(`/scribe/sessions/${lastSessionId}`);
      if (!didNavigate) requestScribeLayoutTourRelaunch(false);
    } catch {
      requestScribeLayoutTourRelaunch(false);
    }
  };

  // Help dropdown content (shared between both modes)
  const helpDropdownContent = (
    <DropdownMenuContent
      className={cn(
        'rounded-lg',
        isMobile
          ? 'w-[var(--radix-dropdown-menu-trigger-width)] min-w-56'
          : showLayoutTourItem
            ? 'w-64'
            : 'w-48'
      )}
      side={isMobile ? 'bottom' : 'right'}
      align="end"
      sideOffset={dropdownSideOffset}
    >
      {showLayoutTourItem && (
        <DropdownMenuItem
          onClick={() => void handleLaunchScribeLayoutTour()}
          data-testid="sidebar-footer-launch-scribe-layout-tour"
        >
          <SparklesIcon />
          {intl.formatMessage({
            defaultMessage: 'Launch new layout tour',
            id: 'E1x+j/IXeI',
            description: 'Help menu action that restarts the Session Page V2 tour',
          })}
        </DropdownMenuItem>
      )}
      {/* Get support opens Intercom by default. Locales without Intercom support can fall back to a localized contact page. */}
      {!isOnPrem &&
        (supportContactUrl ? (
          <DropdownMenuItem asChild>
            <a
              href={supportContactUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sidebar-footer-get-support"
            >
              <MessageCircleIcon />
              {t('sidenav.getSupport')}
            </a>
          </DropdownMenuItem>
        ) : (
          !isIntercomDisabled && (
            <DropdownMenuItem
              onClick={handleHelpClick}
              data-testid="sidebar-footer-get-support"
            >
              <MessageCircleIcon />
              {t('sidenav.getSupport')}
            </DropdownMenuItem>
          )
        ))}
      <DropdownMenuItem asChild>
        <a
          href={getHelpCenterHomeUrl(intl.locale)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="sidebar-footer-help-center"
        >
          <LibraryBigIcon />
          {t('sidenav.helpCenter')}
        </a>
      </DropdownMenuItem>
      {/* Request a feature links to Canny (external), which is stripped from the
          on-prem build — hide the item. */}
      {!isOnPrem && (
        <DropdownMenuItem asChild>
          <a
            href="https://getheidi.canny.io/features"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              navigationRoadmap();
            }}
            data-canny-link
            data-testid="sidebar-footer-request-feature"
          >
            <LightbulbIcon />
            {t('sidenav.requestFeature')}
          </a>
        </DropdownMenuItem>
      )}
      {!isMobile && (
        <DropdownMenuItem
          onClick={() => {
            setIsShortcutsSheetOpen(true);
          }}
          data-testid="sidebar-footer-shortcuts"
        >
          <CommandIcon />
          {t('sidenav.shortcuts')}
          <Kbd size="compact" className="ms-auto">
            S
          </Kbd>
        </DropdownMenuItem>
      )}
      {/* The refer-and-earn affiliate program is a cloud offering — hide on-prem. */}
      {!isUKOrEU && !isOnPrem && isReferralProgramAvailableForLocale(intl.locale) && (
        <DropdownMenuItem
          onClick={() => {
            setIsAffiliateLinkModalOpen(true);
          }}
          data-testid="sidebar-footer-refer-earn"
        >
          <BadgeDollarSignIcon />
          {earnBonusText}
        </DropdownMenuItem>
      )}
      {/* GB regulatory "About" — links to the Heidi Health Regulatory
          Information page. UK build is a medical device, so this only shows for
          UK-region users. */}
      {showAboutHelpItem && (
        <DropdownMenuItem asChild>
          <Link href="/regulatory" data-testid="sidebar-footer-about">
            <InfoIcon />
            {intl.formatMessage({
              defaultMessage: 'About',
              id: 'cOtUlnsnGB',
              description:
                'Sidebar Help dropdown item linking UK users to the Regulatory Information page',
            })}
          </Link>
        </DropdownMenuItem>
      )}
      {displayVersion && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuLabel
            className="px-2.5 py-1.5 font-normal text-text-tertiary"
            data-testid="sidebar-footer-version"
          >
            <TypographyCaption>
              {intl.formatMessage(
                { defaultMessage: 'Version: {version}', id: 't1jv4rBJrl' },
                { version: displayVersion }
              )}
            </TypographyCaption>
          </DropdownMenuLabel>
        </>
      )}
    </DropdownMenuContent>
  );

  // Label-icons icon-only mode — uses SidebarMenuButton with layout="iconOnly"
  // for compact 36px buttons matching the label-icons nav item icon area.
  if (isRibbonRail) {
    return (
      // Bare items — the shared ribbon-rail <ul> is owned by SideNavFooter.
      <>
        {/* Sidebar IA off: Remote/Dictate are the tail of the collapsible group,
            Help/Notifications are pinned, and the footer More tile (in
            SideNavFooter) holds whatever folds.
            Sidebar IA on: there is no footer More tile. Remote is pinned, and
            Dictate, Help and Notifications fold into the primary nav More. */}
        {/* Devices (Chronicle) */}
        {/* Sidebar IA: Remote leaves the web footer (MUL-3702) but stays on desktop (MUL-3755). */}
        {showRemoteInFooter && !isRemoteCollapsed && (
          <SidebarMenuItem>
            <Popover
              open={isDevicePopoverOpen}
              onOpenChange={handleDevicePopoverOpenChange}
            >
              <PopoverTrigger asChild>
                <SidebarMenuButton
                  layout="iconOnly"
                  disabled={isOfflineGated}
                  tooltip={gatedTooltip(devicesLabel)}
                  aria-label={devicesLabel}
                  data-testid="sidebar-footer-devices"
                  isActive={isRouteActive('/devices')}
                >
                  <RemoteDeviceSvg />
                </SidebarMenuButton>
              </PopoverTrigger>
              {showDevicePopover && (
                <PopoverContent
                  side="right"
                  align="end"
                  sideOffset={dropdownSideOffset}
                  className="w-64 text-clip p-1"
                >
                  <DeviceStatusPopover
                    isConnected={isChronicleConnected}
                    isUsbConnected={isUsbConnected}
                    onNavigateToDevices={handleNavigateToDevices}
                  />
                </PopoverContent>
              )}
            </Popover>
          </SidebarMenuItem>
        )}

        {/* Dictation Logs */}
        {isDictationLogsEnabled && !isDictateCollapsed && (
          // asChild keeps this <li> a direct child of the shared <ul> (no wrapper div).
          <DictationLogsSpotlight
            asChild
            enabled={hostsDictateSpotlight}
            {...dictationLogsSpotlightPorts}
          >
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild={!isOfflineGated}
                layout="iconOnly"
                disabled={isOfflineGated}
                tooltip={gatedTooltip(dictationLogsLabel)}
                aria-label={dictationLogsLabel}
                data-testid="sidebar-footer-dictation-logs"
                isActive={isRouteActive('/dictation-logs')}
              >
                {isOfflineGated ? (
                  <Speech />
                ) : (
                  <Link
                    href="/dictation-logs"
                    // No viewport prefetch — Next still prefetches on hover. See ARC-219.
                    prefetch={false}
                    onClick={() => setPendingNavHref('/dictation-logs')}
                  >
                    <Speech />
                  </Link>
                )}
              </SidebarMenuButton>
            </SidebarMenuItem>
          </DictationLogsSpotlight>
        )}

        {/* Help dropdown. Once Help folds into the primary nav More, the
            menu stays mounted on a hidden footer anchor so the More row can
            still open it. */}
        {isHelpHidden && (
          <DropdownMenu modal={false} open={isHelpOpen} onOpenChange={setIsHelpOpen}>
            <DropdownMenuTrigger asChild>
              <CollapsedFooterAnchor />
            </DropdownMenuTrigger>
            {helpDropdownContent}
          </DropdownMenu>
        )}
        {!isHelpHidden && (
          <SidebarMenuItem>
            <DropdownMenu modal={false} open={isHelpOpen} onOpenChange={setIsHelpOpen}>
              <DropdownMenuTrigger asChild disabled={isOfflineGated}>
                <SidebarMenuButton
                  layout="iconOnly"
                  disabled={isOfflineGated}
                  tooltip={gatedTooltip(t('shared.Help'))}
                  aria-label={t('shared.Help')}
                  data-testid="sidebar-footer-help"
                >
                  <HelpCircleIcon />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              {helpDropdownContent}
            </DropdownMenu>
          </SidebarMenuItem>
        )}

        {/* Notifications. The label-icons branch only renders on
            desktop (`!isMobile`), so the bell is always a
            `<PopoverTrigger>` here. Once the bell folds into the primary
            nav More, a hidden footer anchor keeps the popover placeable. */}
        {isNotificationsHidden && (
          <PopoverAnchor asChild>
            <CollapsedFooterAnchor />
          </PopoverAnchor>
        )}
        {!isNotificationsHidden && (
          <SidebarMenuItem>
            <PopoverTrigger asChild>
              <SidebarMenuButton
                layout="iconOnly"
                disabled={isOfflineGated}
                tooltip={gatedTooltip(t('sidenav.notifications'))}
                onClick={handleNotificationsClick}
                aria-label={
                  hasUnreadNotifications
                    ? t('sidenav.notificationsWithUnread', { count: 'new' })
                    : t('sidenav.notifications')
                }
                data-testid="sidebar-footer-notifications"
              >
                <BellIcon />
                {hasUnreadNotifications && (
                  <div
                    className="absolute end-1 top-1 z-20 size-2 rounded-full bg-validation-destructive"
                    aria-hidden="true"
                  />
                )}
              </SidebarMenuButton>
            </PopoverTrigger>
          </SidebarMenuItem>
        )}
      </>
    );
  }

  // Vertical nav items mode (default)
  if (verticalNavItems) {
    return (
      <SidebarMenu>
        {/* Devices (Chronicle) */}
        {/* Sidebar IA: Remote leaves the web footer (MUL-3702) but stays on desktop (MUL-3755). */}
        {showRemoteInFooter && (
          <SidebarMenuItem>
            <Popover
              open={isDevicePopoverOpen}
              onOpenChange={handleDevicePopoverOpenChange}
            >
              <PopoverTrigger asChild>
                <SidebarMenuButton
                  disabled={isOfflineGated}
                  tooltip={gatedTooltip(devicesLabel)}
                  aria-label={devicesLabel}
                  data-testid="sidebar-footer-devices"
                  isActive={isRouteActive('/devices')}
                >
                  <RemoteDeviceSvg />
                  <span>{devicesLabel}</span>
                </SidebarMenuButton>
              </PopoverTrigger>
              {showDevicePopover && (
                <PopoverContent
                  side="right"
                  align="end"
                  sideOffset={dropdownSideOffset}
                  className="w-64 text-clip p-1"
                >
                  <DeviceStatusPopover
                    isConnected={isChronicleConnected}
                    isUsbConnected={isUsbConnected}
                    onNavigateToDevices={handleNavigateToDevices}
                  />
                </PopoverContent>
              )}
            </Popover>
          </SidebarMenuItem>
        )}

        {/* Dictation Logs */}
        {isDictationLogsEnabled && !(isSidebarIAEnabled && isDictateCollapsed) && (
          <DictationLogsSpotlight
            enabled={hostsDictateSpotlight}
            {...dictationLogsSpotlightPorts}
          >
            <NavItem
              label={dictationLogsLabel}
              icon={<Speech />}
              href="/dictation-logs"
              isActive={isRouteActive('/dictation-logs')}
              testId="sidebar-footer-dictation-logs"
            />
          </DictationLogsSpotlight>
        )}

        {/* Help dropdown */}
        {!isHelpHidden && (
          <SidebarMenuItem>
            <DropdownMenu modal={false} open={isHelpOpen} onOpenChange={setIsHelpOpen}>
              <DropdownMenuTrigger asChild disabled={isOfflineGated}>
                <SidebarMenuButton
                  disabled={isOfflineGated}
                  tooltip={gatedTooltip(t('shared.Help'))}
                  aria-label={t('shared.Help')}
                  data-testid="sidebar-footer-help"
                >
                  <HelpCircleIcon />
                  <span>{t('shared.Help')}</span>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              {helpDropdownContent}
            </DropdownMenu>
          </SidebarMenuItem>
        )}

        {/*
          Notifications. Inlined as `SidebarMenuItem + SidebarMenuButton`
          (rather than the shared `<NavItem>`) so `<PopoverTrigger asChild>`
          can wrap the bell directly — `NavItem` doesn't forward refs to its
          root, which `asChild` requires. Mobile skips the popover Root
          entirely (the bell opens the mobile sheet from `onClick`), so
          `NotificationsBellTrigger` degrades to a passthrough there.
        */}
        {!isNotificationsHidden && (
          <SidebarMenuItem>
            <NotificationsBellTrigger isMobile={isMobile}>
              <SidebarMenuButton
                disabled={isOfflineGated}
                tooltip={gatedTooltip(t('sidenav.notifications'))}
                onClick={handleNotificationsClick}
                aria-label={
                  hasUnreadNotifications
                    ? t('sidenav.notificationsWithUnread', { count: 'new' })
                    : t('sidenav.notifications')
                }
                data-testid="sidebar-footer-notifications"
              >
                <span className="relative">
                  <BellIcon className="size-4.5 shrink-0 sm:size-4" />
                  {hasUnreadNotifications && (
                    <div
                      className="absolute -end-1 -top-1 size-2 rounded-full bg-validation-destructive opacity-0 transition-opacity duration-200 ease-in-out group-data-[state=collapsed]/sidebar:opacity-100"
                      aria-hidden="true"
                    />
                  )}
                </span>
                {hasUnreadNotifications ? (
                  <span className="flex items-center">
                    {t('sidenav.notifications')}
                    <div
                      className="ms-2 size-2 rounded-full bg-validation-destructive"
                      aria-label={t('sidenav.notificationsWithUnread', {
                        count: notifications?.length,
                      })}
                    />
                  </span>
                ) : (
                  <span>{t('sidenav.notifications')}</span>
                )}
              </SidebarMenuButton>
            </NotificationsBellTrigger>
          </SidebarMenuItem>
        )}
      </SidebarMenu>
    );
  }

  // Horizontal icon buttons mode with stacking animation
  const isCollapsed = state === 'collapsed' && collapsible === 'icon';
  const transitionConfig = prefersReducedMotion
    ? { duration: 0 }
    : { type: 'spring' as const, stiffness: 700, damping: 53 };

  // Compact icon button class - mobile: 44x44px (WCAG touch target), desktop: 32x32px
  const iconButtonClass = cn(
    '!h-11 !w-11 !justify-center !p-0 sm:!h-8 sm:!w-8 sm:!p-2',
    '[&>svg]:!translate-x-0 [&>svg]:!translate-y-0'
  );

  return (
    <SidebarMenu
      className={cn(
        'relative flex-row gap-1 transition-[gap,margin] duration-200 ease-out group-data-[collapsible=label-icons]:gap-0 motion-reduce:transition-none',
        isCollapsed && 'mt-9'
      )}
    >
      {/* Help button - stacks vertically when collapsed */}
      <SidebarMenuItem asChild>
        <m.li
          className="z-10"
          style={{
            willChange: prefersReducedMotion ? 'auto' : 'transform',
            transform: prefersReducedMotion
              ? 'translateY(0px)'
              : isCollapsed
                ? 'translateY(-36px)'
                : 'translateY(0px)',
          }}
          animate={{ transform: isCollapsed ? 'translateY(-36px)' : 'translateY(0px)' }}
          transition={transitionConfig}
        >
          <DropdownMenu modal={false} open={isHelpOpen} onOpenChange={setIsHelpOpen}>
            <DropdownMenuTrigger asChild disabled={isOfflineGated}>
              <SidebarMenuButton
                disabled={isOfflineGated}
                tooltip={gatedTooltip(t('shared.Help'))}
                showTooltipWhenExpanded
                className={iconButtonClass}
                aria-label={t('shared.Help')}
                data-testid="sidebar-footer-help-button"
              >
                <HelpCircleIcon />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            {helpDropdownContent}
          </DropdownMenu>
        </m.li>
      </SidebarMenuItem>

      {/* Notifications button - slides horizontally when collapsed */}
      <SidebarMenuItem asChild>
        <m.li
          className="z-0"
          style={{
            willChange: prefersReducedMotion ? 'auto' : 'transform',
            transform: prefersReducedMotion
              ? 'translateX(0px)'
              : isCollapsed
                ? 'translateX(-36px)'
                : 'translateX(0px)',
          }}
          animate={{ transform: isCollapsed ? 'translateX(-36px)' : 'translateX(0px)' }}
          transition={transitionConfig}
        >
          <NotificationsBellTrigger isMobile={isMobile}>
            <SidebarMenuButton
              disabled={isOfflineGated}
              tooltip={gatedTooltip(t('sidenav.notifications'))}
              showTooltipWhenExpanded
              className={cn('relative', iconButtonClass)}
              aria-label={
                hasUnreadNotifications
                  ? t('sidenav.notificationsWithUnread', { count: 'new' })
                  : t('sidenav.notifications')
              }
              onClick={handleNotificationsClick}
              data-testid="sidebar-footer-notifications-button"
            >
              <BellIcon />
              {hasUnreadNotifications && (
                <div
                  className="absolute end-1 top-1 size-2 rounded-full bg-validation-destructive"
                  aria-hidden="true"
                />
              )}
            </SidebarMenuButton>
          </NotificationsBellTrigger>
        </m.li>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
